import React from 'react';

const resolveSeparators = ({ groupSeparator, decimalSeparator, intlConfig }) => {
  const isSpanishLocale = intlConfig && intlConfig.locale && intlConfig.locale.toLowerCase().indexOf('es') === 0;

  return {
    groupSeparator: groupSeparator || (isSpanishLocale ? '.' : ','),
    decimalSeparator: decimalSeparator || (isSpanishLocale ? ',' : '.'),
  };
};

const normalizeValue = (value, options) => {
  const {
    groupSeparator,
    decimalSeparator,
    allowDecimals,
    allowNegativeValue,
    prefix,
  } = options;

  const rawValue = value == null ? '' : String(value);
  const trimmedValue = rawValue.trim();
  const isNativeDecimalValue = decimalSeparator !== '.' && /^-?\d+\.\d+$/.test(trimmedValue);
  let normalized = isNativeDecimalValue
    ? trimmedValue
    : rawValue
      .replace(prefix || '', '')
      .replace(new RegExp('\\' + groupSeparator, 'g'), '')
      .replace(decimalSeparator, '.')
      .replace(/[^\d.-]/g, '');

  if (allowNegativeValue === false) {
    normalized = normalized.replace(/-/g, '');
  } else {
    normalized = normalized.replace(/(?!^)-/g, '');
  }

  if (allowDecimals === false) {
    normalized = normalized.replace(/\./g, '');
  } else {
    const firstDecimal = normalized.indexOf('.');
    if (firstDecimal !== -1) {
      normalized = normalized.slice(0, firstDecimal + 1) + normalized.slice(firstDecimal + 1).replace(/\./g, '');
    }
  }

  return normalized;
};

const limitDecimals = (value, decimalsLimit) => {
  if (!decimalsLimit || value.indexOf('.') === -1) {
    return value;
  }

  const parts = value.split('.');
  return parts[0] + '.' + parts[1].slice(0, decimalsLimit);
};

const formatDisplayValue = (value, options) => {
  const { groupSeparator, decimalSeparator, prefix, allowDecimals } = options;
  const normalized = normalizeValue(value, options);

  if (normalized === '' || normalized === '-') {
    return '';
  }

  const isNegative = normalized.charAt(0) === '-';
  const unsigned = isNegative ? normalized.slice(1) : normalized;
  const parts = unsigned.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
  const decimalPart = allowDecimals === false || parts[1] == null ? '' : decimalSeparator + parts[1];

  return (prefix || '') + (isNegative ? '-' : '') + integerPart + decimalPart;
};

const SafeCurrencyInput = ({
  allowDecimals = true,
  allowNegativeValue = true,
  className,
  decimalSeparator,
  decimalsLimit,
  defaultValue,
  disabled,
  groupSeparator,
  intlConfig,
  name,
  onBlur,
  onChange,
  onFocus,
  onValueChange,
  prefix = '',
  value,
  ...props
}) => {
  const separators = resolveSeparators({ groupSeparator, decimalSeparator, intlConfig });
  const options = { ...separators, allowDecimals, allowNegativeValue, prefix };
  const normalizedValue = limitDecimals(normalizeValue(value == null ? defaultValue : value, options), decimalsLimit);
  const displayValue = formatDisplayValue(normalizedValue, options);

  const handleValueChange = (inputValue, name, values) => {
    if (onValueChange) {
      onValueChange(inputValue == null ? '' : inputValue, name, values);
    }
  };

  const handleChange = (event) => {
    const nextValue = limitDecimals(normalizeValue(event.target.value, options), decimalsLimit);
    const floatValue = nextValue === '' || nextValue === '-' ? null : parseFloat(nextValue);
    handleValueChange(nextValue, name, {
      float: isNaN(floatValue) ? null : floatValue,
      formatted: formatDisplayValue(nextValue, options),
      value: nextValue,
    });

    if (onChange) {
      onChange(event);
    }
  };

  return (
    <input
      {...props}
      type="text"
      inputMode={allowDecimals === false ? 'numeric' : 'decimal'}
      name={name}
      disabled={disabled}
      className={className}
      value={displayValue}
      onBlur={onBlur}
      onChange={handleChange}
      onFocus={onFocus}
    />
  );
};

export default SafeCurrencyInput;



// WEBPACK FOOTER //
// ./src/components/SafeCurrencyInput/SafeCurrencyInput.js